/**
 * CTA Dialog Renderer
 *
 * Component that renders all CTA-related dialogs based on context state
 */

'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCTADialogs } from './cta-dialog-context';
import {
  DeathCertificateUploadDialog,
  WillUploadDialog,
  GrantOfRepresentationUploadDialog,
  LetterheadUploadDialog,
  AdditionalDocumentUploadDialog
} from '@/components/documents/dialogs';
import { AddContactDialog } from '@/components/contacts/add-contact-dialog';
import { AddInventoryDialog } from '@/components/inventory/dialogs/add/AddInventoryDialog';
import { EditMatterDialog } from '@/components/matters/edit-matter-dialog';
import { ManageSimpleMatterSheet } from '@/components/matter-details/manage-simple-matter-sheet';
import { useLawFirmUsers } from '@/lib/services/account/hooks';
import { CorrespondenceChatSimplified } from '@/components/correspondence-chat';
import { useMatterCorrespondencesService } from '@/lib/services/correspondences/hooks';
import { useInstitution } from '@/lib/services/institutions/hooks';
import { useMatter } from '@/lib/services/matters/hooks';
import { archiveMatterAction } from '@/lib/services/matters/client';
import { DeleteMatterDialog } from '@/components/matters/delete-matter-dialog';
import { formatMatterTitle } from '@/lib/utils/matter-display';
import { matterKeys } from '@/lib/query-keys';
import { NotificationsWorkflow } from '@/components/workflows/notifications-workflow';
import { CollectionRequestWorkflow } from '@/components/workflows/collection-request-workflow';
import { ViewFormsDialog } from '@/components/institutions/dialogs/view-forms-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from '@/lib/toast';
import { useMatterFilesService } from '@/lib/services/files/hooks';
import type { CoreDocumentUploadHandler } from '@/components/documents/dialogs';
import { useRequestConsolidation } from '@/lib/services/ai-analysis/hooks';
import { usePendingAiReviewStore } from '@/lib/stores/pending-ai-review-store';
import { aiAnalysisProgressKey } from '@/lib/hooks/use-matter-ai-analysis-polling';
import {
  FileTypeSlug,
  detectFileTypeFromName
} from '@/lib/services/files/file-types';
import type { FileType } from '@/lib/services/files/types';
import {
  taskKeys,
  inventoryKeys,
  progressKeys,
  overviewKeys
} from '@/lib/query-keys';
import { MATTER_ESTATE_ADMIN_KEYS } from '@/lib/hooks/use-matter-estate-admin-data';
import { getUserDisplayName } from '@/lib/utils/permissions';

// Wrapper component for correspondence dialog that replicates institution tab's data passing
function CorrespondenceDialogWrapper({
  matterId,
  institutionId,
  draftCorrespondenceId,
  onClose
}: {
  matterId: number;
  institutionId: number;
  draftCorrespondenceId?: number;
  onClose: () => void;
}) {
  // Fetch data exactly like institution tab does
  const { data: institution } = useInstitution(institutionId);
  const { data: matter } = useMatter(matterId);
  const { correspondences, updateCorrespondence } =
    useMatterCorrespondencesService(matterId);

  // Filter correspondence for this institution
  const institutionCorrespondence = correspondences.filter(
    (corr) => corr.institution?.id === institutionId
  );

  // Find the draft correspondence if provided
  const draftCorrespondence = draftCorrespondenceId
    ? correspondences.find((c) => c.id === draftCorrespondenceId)
    : null;

  // Extract the draft content (fragments might be in a different field)
  const draftWithFragments = draftCorrespondence as
    | { fragments?: { mainContent?: string }; body?: string }
    | null
    | undefined;
  const initialDraftContent =
    draftWithFragments?.fragments?.mainContent ||
    draftWithFragments?.body ||
    '';

  // Transform exactly like institution tab does
  const transformedCorrespondence = institutionCorrespondence.map(
    (corr: (typeof institutionCorrespondence)[number]) => {
      const isFromInstitution = corr.type === 'reply';
      const correspondenceWithDirection = corr as typeof corr & {
        direction?: 'inbound' | 'outbound';
      };
      const direction =
        correspondenceWithDirection.direction ||
        (isFromInstitution ? 'inbound' : 'outbound');

      return {
        id: corr.id,
        institutionId: institutionId,
        matterId: matterId,
        subject: corr.subject || '',
        content: corr.body || corr.fragments?.mainContent || '',
        body: corr.body || corr.fragments?.mainContent || '',
        fragments: corr.fragments,
        type: corr.type || 'notification',
        direction,
        status: corr.status || 'sent',
        sentAt: corr.sentAt || corr.updatedAt || corr.createdAt,
        attachments: corr.attachments || [],
        sender: isFromInstitution
          ? corr.institution?.name || institution?.name || 'Institution'
          : corr.lawFirmUser
            ? getUserDisplayName(corr.lawFirmUser)
            : 'You',
        recipient: isFromInstitution
          ? 'You'
          : corr.institution?.name || institution?.name || 'Institution',
        createdAt: corr.createdAt,
        updatedAt: corr.updatedAt
      };
    }
  );

  // Handle send message - updates draft if exists, creates new if not
  const handleSendMessage = async (content: string, subject?: string) => {
    if (draftCorrespondenceId && updateCorrespondence) {
      try {
        // Update the existing draft
        await updateCorrespondence(draftCorrespondenceId, {
          subject: subject || draftCorrespondence?.subject || '',
          mainContent: content
        });
        toast.success('Draft updated');
      } catch (error) {
        console.error('Failed to update draft:', error);
        toast.error('Failed to update draft');
      }
    } else {
      toast.info('Send functionality to be implemented for new messages');
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[90vh] w-[90vw] max-w-5xl flex-col p-0">
        <div className="flex-1 overflow-hidden">
          <CorrespondenceChatSimplified
            matterId={matterId}
            institutionId={institutionId}
            institutionName={institution?.name || 'Institution'}
            institutionFaviconUrl={institution?.faviconUrl || undefined}
            matterReference={matter?.lawFirmInternalReference || undefined}
            deceasedName={
              matter
                ? `${matter.decedentFirstName} ${matter.decedentLastName}`
                : undefined
            }
            fullHeight={true}
            externalMessages={transformedCorrespondence}
            onSendMessage={handleSendMessage}
            autoFocus={true}
            initialMessage={initialDraftContent}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Wrapper for EditMatterDialog that includes cached users
function EditMatterDialogWithUsers(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number;
  onSuccess?: () => void;
  onRequestDelete?: () => void;
}) {
  const {
    users: lawFirmUsers,
    isLoading: usersLoading,
    error: _usersError
  } = useLawFirmUsers();

  return (
    <EditMatterDialog
      {...props}
      lawFirmUsers={lawFirmUsers}
      usersLoading={usersLoading}
    />
  );
}

// Wrapper for ManageSimpleMatterSheet — fetches the matter data the sheet
// needs, mirroring EditMatterDialogWithUsers above (LF-5692).
function ManageSimpleMatterSheetWithData(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number;
  onRequestDelete?: () => void;
}) {
  const { data: matter } = useMatter(props.matterId);

  return (
    <ManageSimpleMatterSheet
      matterId={props.matterId}
      matter={matter ?? null}
      open={props.open}
      onOpenChange={props.onOpenChange}
      onRequestDelete={props.onRequestDelete}
    />
  );
}

/**
 * The matter page's delete (VALE-1927), opened from either manage drawer once it has
 * closed. Leaving the page follows both outcomes, a frame later so the dialog's close
 * commits before the route unmounts it (`components/CLAUDE.md`, body-lock sequencing).
 */
function DeleteMatterDialogForMatter({
  matterId,
  onClose
}: {
  matterId: number;
  onClose: () => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: matter } = useMatter(matterId);

  if (!matter) return null;

  const leave = () => {
    onClose();
    requestAnimationFrame(() => router.push('/all'));
  };

  const handleArchive = async () => {
    const result = await archiveMatterAction(matterId);
    queryClient.invalidateQueries({ queryKey: matterKeys.all });
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
    if (!result.success) {
      toast.error(result.error || 'Failed to archive matter');
      return;
    }
    toast.success('Matter archived');
    leave();
  };

  return (
    <DeleteMatterDialog
      matterId={matterId}
      matterTitle={formatMatterTitle(matter)}
      reference={matter.lawFirmInternalReference}
      isArchived={!!matter.isArchived}
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onArchive={handleArchive}
      onDeleted={leave}
    />
  );
}

interface WorkflowDialogState {
  open: boolean;
  matterId: number;
}

function NotificationsWorkflowDialog({
  dialog,
  queryClient,
  onClose
}: {
  dialog: WorkflowDialogState;
  queryClient: QueryClient;
  onClose: () => void;
}) {
  const handleClose = () => {
    console.log(
      '[NotificationsWorkflow] Closing workflow for matter:',
      dialog.matterId
    );
    invalidateMatterTaskCaches(
      queryClient,
      dialog.matterId,
      'NotificationsWorkflow'
    );
    console.log('[NotificationsWorkflow] Closing dialog');
    onClose();
  };

  return (
    <Dialog
      open={dialog.open}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="h-[90vh] w-[95vw] max-w-7xl p-0">
        <div className="h-full overflow-y-auto">
          <NotificationsWorkflow
            matterId={dialog.matterId}
            onComplete={handleClose}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CollectionWorkflowDialog({
  dialog,
  queryClient,
  onClose
}: {
  dialog: WorkflowDialogState;
  queryClient: QueryClient;
  onClose: () => void;
}) {
  const handleClose = () => {
    console.log(
      '[CollectionWorkflow] Closing workflow for matter:',
      dialog.matterId
    );
    invalidateMatterTaskCaches(
      queryClient,
      dialog.matterId,
      'CollectionWorkflow'
    );
    console.log('[CollectionWorkflow] Closing dialog');
    onClose();
  };

  return (
    <Dialog
      open={dialog.open}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="h-[90vh] w-[95vw] max-w-7xl p-0">
        <div className="h-full overflow-y-auto">
          <CollectionRequestWorkflow
            matterId={dialog.matterId}
            onComplete={handleClose}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function invalidateMatterTaskCaches(
  queryClient: QueryClient,
  matterId: number,
  logPrefix: string
) {
  console.log(
    `[${logPrefix}] Invalidating tasks-dashboard-data (grouped format)`
  );
  queryClient.invalidateQueries({
    queryKey: taskKeys.dashboard(matterId, 0)
  });
  console.log(
    `[${logPrefix}] Invalidating tasks-dashboard-data (aggregated format)`
  );
  queryClient.invalidateQueries({
    queryKey: taskKeys.dashboard(matterId, 1)
  });
  console.log(`[${logPrefix}] Invalidating institution-tasks-dashboard`);
  queryClient.invalidateQueries({
    queryKey: taskKeys.institutionDashboard(matterId)
  });
  console.log(`[${logPrefix}] Invalidating inventory cache`);
  queryClient.invalidateQueries({
    queryKey: inventoryKeys.forMatter(matterId)
  });
  queryClient.invalidateQueries({
    queryKey: MATTER_ESTATE_ADMIN_KEYS.overview(matterId)
  });
  queryClient.invalidateQueries({
    queryKey: overviewKeys.forMatter(matterId)
  });
}

export function CTADialogRenderer() {
  const queryClient = useQueryClient();
  const {
    fileUploadDialog,
    closeFileUploadDialog,
    contactCreateDialog,
    closeContactCreateDialog,
    inventoryCreateDialog,
    closeInventoryCreateDialog,
    correspondenceDialog,
    closeCorrespondenceDialog,
    estateDetailsDialog,
    closeEstateDetailsDialog,
    manageMatterDialog,
    closeManageMatterDialog,
    notificationsWorkflowDialog,
    closeNotificationsWorkflow,
    collectionWorkflowDialog,
    closeCollectionWorkflow,
    viewFormsDialog,
    closeViewFormsDialog
  } = useCTADialogs();

  const [deleteMatterId, setDeleteMatterId] = useState<number | null>(null);
  const requestDelete = (matterId: number, closeDrawer: () => void) => () => {
    closeDrawer();
    requestAnimationFrame(() => setDeleteMatterId(matterId));
  };

  // Use the file upload service - we need to call the hook unconditionally
  // Use matter ID 0 when no dialog is open (service won't be used anyway)
  const filesService = useMatterFilesService(fileUploadDialog?.matterId || 0);
  const requestConsolidation = useRequestConsolidation(
    fileUploadDialog?.matterId || 0
  );
  const setTriggerSource = usePendingAiReviewStore((s) => s.setTriggerSource);

  // Handle contact creation completion
  const handleContactCreateComplete = () => {
    // Don't show toast here - the mutation hook already shows one
    closeContactCreateDialog();
  };

  // Handle inventory creation completion
  const handleInventoryCreateComplete = () => {
    // Success toast is already handled by the mutation hook with detailed information
    // No need for duplicate toast here
    closeInventoryCreateDialog();
  };

  // Render the appropriate dialog based on file type
  const renderFileUploadDialog = () => {
    if (!fileUploadDialog) return null;

    /**
     * VALE-1460: this host's `onUpload` used to take `(files)` only, the same
     * arity bug PR #3822 fixed on the Grant tab (see
     * `core-document-upload-handler.ts`) — so "Upload & extract" from a task
     * CTA filed the document and silently never extracted. Typed against
     * `CoreDocumentUploadHandler` and forwarding `extract` as
     * `analysisRequested` + requesting consolidation, mirroring
     * `documents-tab-content.tsx`'s `handleCoreDocumentUpload`.
     *
     * This same handler also serves the letterhead and additional-document
     * dialogs, which ask no extraction question at all — `base-upload-dialog`
     * only renders the "Upload & extract" button when the dialog's config sets
     * `buttons.uploadAndExtract` (`dialog-configs.tsx`), which neither of those
     * two configs does. So `extract` is structurally always `false` for them,
     * and they fall through the `if (!extract) return;` below exactly like a
     * plain "Upload" answer on a choice dialog would — filing only, no
     * consolidation. That is the deliberate decision for item 4: no
     * special-casing needed, because the non-asking dialogs can never produce
     * `extract: true` in the first place.
     *
     * No `openExtractProgress` here (unlike the Documents tab): this host has
     * no such surface to open, and the matter layout's own
     * `useMatterAiAnalysisPolling` still picks up the invalidated progress key
     * and carries the in-flight toast, exactly as the Grant tab's version of
     * this fix relies on.
     */
    const commonProps = {
      open: fileUploadDialog.open,
      onOpenChange: (open: boolean) => {
        if (!open) closeFileUploadDialog();
      },
      onUpload: (async (files, helpers, extract) => {
        // Process files one by one with proper loading states
        let hasError = false;

        for (const fileData of files) {
          // Show loading toast immediately
          const loadingToastId = toast.loading(
            `Uploading ${fileData.file.name}...`
          );

          try {
            // Map CTA file types to the correct FileTypeSlug values
            let fileType: string;

            // Override with the specific file type from the CTA dialog if present
            if (fileUploadDialog.fileType) {
              switch (fileUploadDialog.fileType) {
                case 'death-certificate':
                  fileType = FileTypeSlug.DEATH_CERTIFICATE;
                  break;
                case 'will':
                  fileType = FileTypeSlug.WILL;
                  break;
                case 'grant-of-representation':
                  fileType = FileTypeSlug.GRANT_OF_representation;
                  break;
                case 'letterhead':
                  fileType = FileTypeSlug.LETTERHEAD;
                  break;
                default:
                  fileType = FileTypeSlug.ADDITIONAL_DOCUMENT;
              }
            } else if (
              fileData.fileType &&
              Object.values(FileTypeSlug).includes(
                fileData.fileType as FileTypeSlug
              )
            ) {
              fileType = fileData.fileType;
            } else {
              const detected = detectFileTypeFromName(fileData.file.name);
              fileType = detected || FileTypeSlug.ADDITIONAL_DOCUMENT;
            }

            // Use the files service to upload with proper loading state
            await filesService.uploadFile(fileData.file, fileType as FileType, {
              displayName: fileData.file.name,
              isCertifiedCopy: fileData.isCertifiedCopy,
              // Filing a document is not consent to read it (VALE-757): the
              // dialog asked, and this is the answer.
              analysisRequested: extract,
              // Real per-file progress for the dialog row (LF-4771).
              onProgress: (percent) =>
                helpers?.onFileProgress(fileData.file, percent),
              signal: helpers?.signal
            });

            // Dismiss the loading toast
            toast.dismiss(loadingToastId);
            // Success toast will be shown by the mutation hook
          } catch (error) {
            hasError = true;
            // Dismiss the loading toast
            toast.dismiss(loadingToastId);
            // Error is already handled by the mutation hook with toast
            console.error('Upload failed:', error);
          }
        }

        if (!hasError) {
          closeFileUploadDialog();
          // Invalidate progress query so the timeline updates
          if (fileUploadDialog.matterId) {
            queryClient.invalidateQueries({
              queryKey: progressKeys.forMatter(fileUploadDialog.matterId)
            });
          }
        }

        // Filing only, or a failed upload: nothing else to do. No
        // consolidation, so no matter data moves and no review is raised.
        if (!extract || hasError) return;

        // Typed-modal trigger source: a run that finds nothing still says so,
        // because the lawyer asked for this and silence would read as a
        // failure.
        setTriggerSource('core');
        try {
          await requestConsolidation.mutateAsync();
          await queryClient.invalidateQueries({
            queryKey: aiAnalysisProgressKey(fileUploadDialog.matterId)
          });
        } catch (err) {
          console.warn('Consolidation request failed:', err);
          toast.warning(
            'Extraction could not be started. Your document is still saved.'
          );
        }
      }) satisfies CoreDocumentUploadHandler
    };

    switch (fileUploadDialog.fileType) {
      case 'death-certificate':
        return <DeathCertificateUploadDialog {...commonProps} />;
      case 'will':
        return <WillUploadDialog {...commonProps} />;
      case 'grant-of-representation':
        return (
          <GrantOfRepresentationUploadDialog
            {...commonProps}
            matterId={fileUploadDialog.matterId}
          />
        );
      case 'letterhead':
        return <LetterheadUploadDialog {...commonProps} />;
      default:
        // Default to additional document dialog
        return <AdditionalDocumentUploadDialog {...commonProps} />;
    }
  };

  return (
    <>
      {/* File Upload Dialog - Specific dialog based on file type */}
      {renderFileUploadDialog()}

      {/* Contact Creation Dialog - Now with controlled open state */}
      {contactCreateDialog && (
        <AddContactDialog
          open={contactCreateDialog.open}
          onOpenChange={(open) => {
            if (!open) closeContactCreateDialog();
          }}
          onSuccess={handleContactCreateComplete}
          initialIsBeneficiary={contactCreateDialog.isBeneficiary}
          matterId={contactCreateDialog.matterId} // Pass the matter ID from dialog state
        />
      )}

      {/* Inventory Creation Dialog */}
      {inventoryCreateDialog && (
        <AddInventoryDialog
          open={inventoryCreateDialog.open}
          onOpenChange={(open) => {
            if (!open) closeInventoryCreateDialog();
          }}
          onInventoryAdded={handleInventoryCreateComplete}
          matterId={String(inventoryCreateDialog.matterId)}
          initialInventoryType={inventoryCreateDialog.inventoryType}
        />
      )}

      {/* Correspondence Dialog - Using working implementation */}
      {correspondenceDialog && (
        <CorrespondenceDialogWrapper
          matterId={correspondenceDialog.matterId}
          institutionId={correspondenceDialog.institutionId}
          draftCorrespondenceId={correspondenceDialog.parentCorrespondenceId}
          onClose={() => {
            const matterId = correspondenceDialog.matterId;
            closeCorrespondenceDialog();
            queryClient.invalidateQueries({
              queryKey: MATTER_ESTATE_ADMIN_KEYS.overview(matterId)
            });
            queryClient.invalidateQueries({
              queryKey: overviewKeys.forMatter(matterId)
            });
          }}
        />
      )}

      {/* Estate Details Dialog - Using existing EditMatterDialog */}
      {estateDetailsDialog && (
        <EditMatterDialogWithUsers
          open={estateDetailsDialog.open}
          onOpenChange={(open) => {
            if (!open) closeEstateDetailsDialog();
          }}
          matterId={estateDetailsDialog.matterId}
          onSuccess={closeEstateDetailsDialog}
          onRequestDelete={requestDelete(
            estateDetailsDialog.matterId,
            closeEstateDetailsDialog
          )}
        />
      )}

      {/* Manage Matter Sheet (non-estate) - rendered globally so the header
          card opens it from every tab, matching the estate details dialog
          above (LF-5692) */}
      {manageMatterDialog && (
        <ManageSimpleMatterSheetWithData
          open={manageMatterDialog.open}
          matterId={manageMatterDialog.matterId}
          onOpenChange={(open) => {
            if (!open) closeManageMatterDialog();
          }}
          onRequestDelete={requestDelete(
            manageMatterDialog.matterId,
            closeManageMatterDialog
          )}
        />
      )}

      {deleteMatterId !== null && (
        <DeleteMatterDialogForMatter
          matterId={deleteMatterId}
          onClose={() => setDeleteMatterId(null)}
        />
      )}

      {/* Notifications Workflow Dialog */}
      {notificationsWorkflowDialog && (
        <NotificationsWorkflowDialog
          dialog={notificationsWorkflowDialog}
          queryClient={queryClient}
          onClose={closeNotificationsWorkflow}
        />
      )}

      {/* Collection Workflow Dialog */}
      {collectionWorkflowDialog && (
        <CollectionWorkflowDialog
          dialog={collectionWorkflowDialog}
          queryClient={queryClient}
          onClose={closeCollectionWorkflow}
        />
      )}

      {/* View Forms Dialog */}
      {viewFormsDialog && (
        <ViewFormsDialog
          open={viewFormsDialog.open}
          onOpenChange={(open) => {
            if (!open) closeViewFormsDialog();
          }}
          matterId={viewFormsDialog.matterId}
          institutionId={viewFormsDialog.institutionId}
          institutionName={viewFormsDialog.institutionName}
        />
      )}
    </>
  );
}
